<template>
    <div>
        <form-modal
            ref="formModal"
            :title="modalTitle"
            size="xl"
            :in-editing="isEdit"
            :handle-close="handleClose"
        >
            <template v-slot:form>
                <div
                    v-if="pillarHasId"
                    class="loja-perfeita-modal-toolbar d-flex justify-content-end align-items-center flex-nowrap w-100 mb-2"
                >
                    <edit-label-button
                        v-if="!isEdit"
                        :title="getI18n(i18nKey, 'TITLES.pilar')"
                        :disabled="submitLoading"
                        @onClick="enterEditMode"
                    />
                    <delete-label-button
                        :title="getI18n(i18nKey, 'TITLES.pilar')"
                        :disabled="submitLoading"
                        @onClick="confirmDeleteCurrentPillar"
                    />
                </div>
                <b-tabs
                    v-model="pillarTabIndex"
                    class="tabs-pdv"
                >
                    <b-tab :title="getI18n(i18nKey, 'TITLES.dados_pilar')">
                        <form @submit.prevent="handleSubmitPillar">
                            <b-row>
                                <b-col md="12">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.nome')"
                                        label-for="pillar-name"
                                        class="required label-pdv"
                                    >
                                        <b-form-input
                                            id="pillar-name"
                                            v-model.trim="formData.name"
                                            class="input-pdv-blue"
                                            type="text"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="6">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.pontuacao_pilar')"
                                        label-for="pillar-weight"
                                        class="label-pdv"
                                    >
                                        <b-form-input
                                            id="pillar-weight"
                                            v-model.number="formData.weight"
                                            class="input-pdv-blue"
                                            type="number"
                                            min="0"
                                            step="any"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                                <b-col md="6">
                                    <b-form-group
                                        :label="getI18n(i18nKey, 'TITLES.meta_alcance_pilar')"
                                        label-for="pillar-target-reach"
                                        class="label-pdv"
                                    >
                                        <b-form-input
                                            id="pillar-target-reach"
                                            v-model.number="formData.target_reach_percent"
                                            class="input-pdv-blue"
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="any"
                                            :placeholder="'80'"
                                            :disabled="submitLoading || !isFormEditable"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <b-row>
                                <b-col md="12">
                                    <b-alert show variant="light" class="border small mb-3">
                                        <span class="d-block mb-2">{{ getI18n(i18nKey, 'INTROS.pilar_pontuacao_hint') }}</span>
                                        <span class="d-block mb-0">{{ getI18n(i18nKey, 'INTROS.meta_alcance_pilar') }}</span>
                                    </b-alert>
                                </b-col>
                            </b-row>
                            <b-form-group
                                :label="getI18n(i18nKey, 'TITLES.recomendacoes_resumo_executivo')"
                                class="label-pdv"
                            >
                                <template v-slot:description>
                                    <span class="text-muted small">{{ getI18n(i18nKey, 'INTROS.recomendacoes_resumo_executivo') }}</span>
                                </template>
                                <div
                                    v-for="(recLine, recIdx) in formData.recommendation_lines"
                                    :key="'rec-line-' + recIdx"
                                    class="d-flex align-items-center mb-2"
                                >
                                    <b-form-input
                                        v-model="formData.recommendation_lines[recIdx]"
                                        class="input-pdv-blue flex-grow-1 mr-2"
                                        type="text"
                                        :disabled="submitLoading || !isFormEditable"
                                    />
                                    <button
                                        v-if="isFormEditable"
                                        type="button"
                                        class="btn btn-link p-0 border-0 text-danger flex-shrink-0 loja-perfeita-rec-line-delete"
                                        :disabled="submitLoading"
                                        :title="getI18n(i18nKey, 'BUTTONS.excluir')"
                                        :aria-label="getI18n(i18nKey, 'BUTTONS.excluir')"
                                        @click="removeRecommendationLine(recIdx)"
                                    >
                                        <i class="fe-trash-2" aria-hidden="true"></i>
                                    </button>
                                </div>
                                <b-button
                                    v-if="isFormEditable"
                                    size="sm"
                                    class="btn-pdv-gradient-blue-transparent"
                                    type="button"
                                    :disabled="submitLoading"
                                    @click="addRecommendationLine"
                                >
                                    <i class="fe-plus-circle" aria-hidden="true"></i>
                                    {{ getI18n(i18nKey, 'BUTTONS.adicionar_linha_recomendacao') }}
                                </b-button>
                            </b-form-group>
                            <div class="d-flex justify-content-end gap-2 mt-3">
                                <b-button
                                    class="btn btn-pdv-gradient-blue-transparent"
                                    type="button"
                                    :disabled="submitLoading"
                                    @click="handleClose"
                                >
                                    {{ getI18n(i18nKey, 'BUTTONS.cancelar') }}
                                </b-button>
                                <b-button
                                    class="btn btn-pdv-gradient-blue ml-3"
                                    type="submit"
                                    :disabled="submitLoading || !formData.name || formData.weight === null || formData.weight === undefined || !isFormEditable"
                                >
                                    <b-spinner v-if="submitLoading" small class="mr-1" />
                                    {{ getI18n(i18nKey, 'BUTTONS.salvar') }}
                                </b-button>
                            </div>
                        </form>
                    </b-tab>
                    <b-tab
                        :title="getI18n(i18nKey, 'TITLES.criterios')"
                        :disabled="!pillarHasId"
                    >
                        <div v-if="!pillarHasId" class="mt-2">
                            <b-alert variant="light" show>
                                {{ getI18n(i18nKey, 'MESSAGES.save_pillar_before_criterios') }}
                            </b-alert>
                        </div>
                        <div v-else class="mt-2">
                            <div class="d-flex justify-content-between align-items-center mb-4">
                                <p class="text-muted mb-0">
                                    {{ getI18n(i18nKey, 'INTROS.criterios_pilar') }}
                                </p>
                                <b-button
                                    size="sm"
                                    class="btn-pdv-gradient-blue"
                                    @click="openCriterionModal(null)"
                                >
                                    <i class="fe-plus-circle" aria-hidden="true"></i>
                                    {{ getI18n(i18nKey, 'BUTTONS.adicionar_criterio') }}
                                </b-button>
                            </div>
                            <loja-perfeita-list-skeleton
                                v-if="criteriaLoading"
                                :rows="5"
                                :columns="4"
                            />
                            <b-table
                                v-else
                                :items="criteriaForPillar"
                                :fields="criteriaFieldsLocal"
                                striped
                                responsive
                                show-empty
                                :empty-text="getI18n(i18nKey, 'MESSAGES.empty_criterios')"
                            >
                                <template v-slot:cell(rule_type)="{ item }">
                                    {{ ruleTypeLabel(item.rule_type) }}
                                </template>
                                <template v-slot:cell(actions)="{ item }">
                                    <div class="table-button-container text-right">
                                        <more-table-button @onClick="openCriterionModal(item)" />
                                    </div>
                                </template>
                            </b-table>
                        </div>
                    </b-tab>
                    <b-tab
                        :title="getI18n(i18nKey, 'TITLES.segmentacao')"
                        :disabled="!pillarHasId"
                    >
                        <div class="mt-2">
                            <loja-perfeita-conditions-editor
                                v-if="pillarHasId"
                                ref="pillarConditionsEditor"
                                parent-type="pillar"
                                :parent-id="formData.id"
                                :hint="getI18n(i18nKey, 'MESSAGES.segmentacao_hint_pilar')"
                            />
                        </div>
                    </b-tab>
                </b-tabs>
            </template>
        </form-modal>

        <loja-perfeita-criterion-modal
            v-if="campaignId && pillarHasId"
            ref="criterionModal"
            :campaign-id="campaignId"
            :pillar-id="formData.id"
            :criteria-for-depends-on="allCriteria"
            @refresh="onCriterionRefresh"
            @open-questions="openQuestionsModal"
        />

        <b-modal
            ref="deleteConfirmModal"
            centered
            size="md"
            @hidden="deleteConfirmCallback = null"
        >
            <template v-slot:modal-header>
                <h4 class="modal-title description">
                    {{ deleteConfirmTitle }}
                </h4>
                <button type="button" class="close" aria-label="Close" @click="closeDeleteConfirm">
                    <i class="fe-x-circle icon-blue" aria-hidden="true"></i>
                </button>
            </template>
            <div class="message">
                {{ deleteConfirmBody }}
            </div>
            <template v-slot:modal-footer>
                <div class="col text-center ml-3 mr-3">
                    <delete-button
                        class="mb-1"
                        :busy="deleteConfirmBusy"
                        :title="getI18n(i18nKey, 'BUTTONS.excluir')"
                        @onClick="executeDeleteConfirm"
                    />
                    <cancel-label-button @onClick="closeDeleteConfirm" />
                </div>
            </template>
        </b-modal>

        <b-modal
            v-model="questionsModalVisible"
            :title="questionsModalTitle"
            size="lg"
            hide-footer
            @hidden="onQuestionsModalHidden"
        >
            <div v-if="selectedCriterionForQuestions && !criterionQuestionsLoading" class="mb-3">
                <b-button size="sm" class="btn-pdv-gradient-blue" @click="openQuestionModal()">
                    <i class="fe-plus-circle"></i>
                    {{ getI18n(i18nKey, 'BUTTONS.adicionar_questao') }}
                </b-button>
            </div>
            <loja-perfeita-list-skeleton
                v-if="criterionQuestionsLoading"
                :rows="6"
                :columns="7"
            />
            <b-table
                v-else
                :items="criterionQuestions"
                :fields="questionFieldsLocal"
                striped
                responsive
                show-empty
                :empty-text="getI18n(i18nKey, 'MESSAGES.empty_questoes')"
            >
                <template v-slot:cell(scopeLabel)="{ item }">
                    {{ scopeLabelForTask(item) }}
                </template>
                <template v-slot:cell(condition_operator)="{ item }">
                    {{ conditionOperatorLabel(item.condition_operator) }}
                </template>
                <template v-slot:cell(condition_value)="{ item }">
                    {{ item.conditionValueDisplay ?? '—' }}
                </template>
                <template v-slot:cell(actions)="{ item }">
                    <div class="table-button-container text-right">
                        <more-table-button @onClick="openQuestionModal(item)" />
                    </div>
                </template>
            </b-table>
            <loja-perfeita-criterion-question-modal
                v-if="selectedCriterionForQuestions"
                ref="questionModal"
                :criterion-id="selectedCriterionForQuestions.id"
                :campaign-id="campaignId"
                :criterion-weight="selectedCriterionForQuestions.weight"
                :sibling-questions-weight-sum="questionSiblingWeightSum"
                :sibling-criterion-tasks="criterionQuestions"
                @refresh="loadCriterionQuestions"
            />
        </b-modal>
    </div>
</template>

<script>
import FormModal from '@src/components/FormModal.vue'
import LojaPerfeitaService from '@src/services/dashboards/LojaPerfeitaService'
import LojaPerfeitaCriterionModal from './lojaPerfeitaCriterionModal.vue'
import LojaPerfeitaCriterionQuestionModal from './lojaPerfeitaCriterionQuestionModal.vue'
import MoreTableButton from '@/src/components/MoreTableButton.vue'
import EditLabelButton from '@/src/components/EditLabelButton.vue'
import DeleteLabelButton from '@/src/components/DeleteLabelButton.vue'
import DeleteButton from '@src/components/DeleteButton.vue'
import CancelLabelButton from '@src/components/CancelLabelButton.vue'
import Shared from '@src/mixins/shared.vue'
import swalFeedback from '@src/mixins/swalFeedback'
import {
    effectiveCriterionTaskWeight,
    formatConditionValueForDisplay,
    getQuestionKind,
} from '@src/utils/lojaPerfeitaQuestionRuleUi'
import LojaPerfeitaListSkeleton from '@src/components/dashboards/lojaPerfeita/LojaPerfeitaListSkeleton.vue'
import LojaPerfeitaConditionsEditor from '@src/components/dashboards/lojaPerfeita/LojaPerfeitaConditionsEditor.vue'

const i18nKey = 'LOJA_PERFEITA'
const defaultForm = () => ({
    id: 0,
    name: '',
    weight: 0,
    target_reach_percent: null,
    recommendation_lines: [],
})

export default {
    components: {
        FormModal,
        MoreTableButton,
        EditLabelButton,
        DeleteLabelButton,
        LojaPerfeitaCriterionModal,
        LojaPerfeitaCriterionQuestionModal,
        DeleteButton,
        CancelLabelButton,
        LojaPerfeitaListSkeleton,
        LojaPerfeitaConditionsEditor,
    },
    mixins: [Shared, swalFeedback],
    props: { campaignId: { type: [String, Number], required: true } },
    data() {
        return {
            i18nKey,
            submitLoading: false,
            formData: defaultForm(),
            pillarTabIndex: 0,
            allCriteria: [],
            deleteConfirmTitle: '',
            deleteConfirmBody: '',
            deleteConfirmCallback: null,
            deleteConfirmBusy: false,
            selectedCriterionForQuestions: null,
            criterionQuestions: [],
            criterionQuestionsLoading: false,
            criteriaLoading: false,
            questionsModalVisible: false,
            questionSiblingWeightSum: 0,
            isEdit: false,
            criteriaFieldsLocal: [
                { key: 'name', label: this.getI18n(i18nKey, 'TITLES.nome') },
                { key: 'rule_type', label: this.getI18n(i18nKey, 'TITLES.tipo_regra') },
                { key: 'weight', label: this.getI18n(i18nKey, 'TITLES.pontuacao') },
                { key: 'actions', label: this.getI18n(i18nKey, 'TITLES.acoes') },
            ],
            questionFieldsLocal: [
                { key: 'label', label: this.getI18n(i18nKey, 'TITLES.questao') },
                { key: 'depends_on_label', label: this.getI18n(i18nKey, 'TITLES.question_depends_on_col') },
                { key: 'scopeLabel', label: this.getI18n(i18nKey, 'TITLES.escopo') },
                { key: 'weight', label: this.getI18n(i18nKey, 'TITLES.pontuacao') },
                { key: 'condition_operator', label: this.getI18n(i18nKey, 'FIELDS.condition_operator') },
                { key: 'condition_value', label: this.getI18n(i18nKey, 'FIELDS.condition_value') },
                { key: 'actions', label: this.getI18n(i18nKey, 'TITLES.acoes') },
            ],
        }
    },
    computed: {
        pillarHasId() {
            return Boolean(this.formData.id && Number(this.formData.id) > 0)
        },
        modalTitle() {
            return this.formData.id
                ? this.getI18n(i18nKey, 'BUTTONS.editar_pilar')
                : this.getI18n(i18nKey, 'BUTTONS.adicionar_pilar')
        },
        criteriaForPillar() {
            return this.allCriteria.filter((c) => Number(c.pillar_id) === Number(this.formData.id))
        },
        questionsModalTitle() {
            if (!this.selectedCriterionForQuestions) return ''
            return `${this.getI18n(i18nKey, 'TITLES.questoes')} — ${this.selectedCriterionForQuestions.name}`
        },
        isFormEditable() {
            return !this.pillarHasId || this.isEdit
        },
    },
    watch: {
        pillarTabIndex(val) {
            if (val === 1 && this.pillarHasId) {
                this.loadCampaignCriteria()
            }
            if (val === 2 && this.pillarHasId) {
                this.loadPillarConditions()
            }
        },
    },
    methods: {
        showModal() {
            this.submitLoading = false
            this.pillarTabIndex = 0
            this.$refs.formModal.show()
            this.$nextTick(() => {
                if (this.pillarHasId) {
                    this.loadCampaignCriteria()
                }
            })
        },
        replaceModalData(data) {
            const merged = { ...defaultForm(), ...data }
            if (merged.weight == null && merged.max_score != null) {
                merged.weight = Number(merged.max_score) || 0
            }
            delete merged.max_score
            if (!Array.isArray(merged.recommendation_lines)) {
                merged.recommendation_lines = []
            } else {
                merged.recommendation_lines = merged.recommendation_lines.map((l) => {
                    if (typeof l === 'string') {
                        return l
                    }
                    return l && l.body != null ? String(l.body) : ''
                })
            }
            if (merged.target_reach_percent === undefined) {
                merged.target_reach_percent = null
            }
            this.formData = merged
            this.pillarTabIndex = 0
            this.isEdit = !(this.formData.id && Number(this.formData.id) > 0)
            this.allCriteria = []
        },
        handleClose() {
            this.$refs.formModal.hide()
        },
        enterEditMode() {
            this.isEdit = true
            this.pillarTabIndex = 0
            this.$nextTick(() => {
                const el = document.getElementById('pillar-name')
                if (el) el.focus()
            })
        },
        confirmDeleteCurrentPillar() {
            if (!this.pillarHasId) return
            const pillar = { ...this.formData }
            this.openDeleteConfirm(
                this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_pilar_title'),
                this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_pilar'),
                () => LojaPerfeitaService.deletePillar(pillar.id).then(() => {
                    this.successFeedback(this.getI18n(i18nKey, 'TITLES.pilar'), this.getI18n(i18nKey, 'MESSAGES.config_salva'))
                    this.handleClose()
                    this.$emit('refresh')
                })
            )
        },
        loadPillarConditions() {
            if (!this.pillarHasId) return
            if (this.$refs.pillarConditionsEditor) {
                this.$refs.pillarConditionsEditor.loadConditions()
                this.$refs.pillarConditionsEditor.loadDimensions()
            }
        },
        loadCampaignCriteria() {
            if (!this.campaignId) return
            this.criteriaLoading = true
            LojaPerfeitaService.getCriteria(this.campaignId)
                .then((res) => {
                    this.allCriteria = res.data || []
                })
                .catch(() => {
                    this.allCriteria = []
                })
                .finally(() => {
                    this.criteriaLoading = false
                })
        },
        onQuestionsModalHidden() {
            this.selectedCriterionForQuestions = null
            this.criterionQuestions = []
            this.criterionQuestionsLoading = false
        },
        addRecommendationLine() {
            if (!Array.isArray(this.formData.recommendation_lines)) {
                this.$set(this.formData, 'recommendation_lines', [])
            }
            this.formData.recommendation_lines.push('')
        },
        removeRecommendationLine(index) {
            if (!Array.isArray(this.formData.recommendation_lines)) return
            this.formData.recommendation_lines.splice(index, 1)
        },
        pillarPayloadForSave() {
            const payload = {
                name: this.formData.name,
                weight: Number(this.formData.weight) || 0,
            }
            const tr = this.formData.target_reach_percent
            if (tr === '' || tr === null || tr === undefined || Number.isNaN(Number(tr))) {
                payload.target_reach_percent = null
            } else {
                payload.target_reach_percent = Number(tr)
            }
            payload.recommendation_lines = (this.formData.recommendation_lines || [])
                .map((s) => (typeof s === 'string' ? s.trim() : ''))
                .filter((s) => s.length > 0)
            return payload
        },
        handleSubmitPillar() {
            if (!this.formData.name || this.submitLoading || !this.isFormEditable) return
            const wasNew = !this.pillarHasId
            this.submitLoading = true
            const payload = this.pillarPayloadForSave()
            const promise = this.formData.id
                ? LojaPerfeitaService.updatePillar(this.formData.id, payload)
                : LojaPerfeitaService.createPillar(this.campaignId, payload)
            promise
                .then((res) => {
                    const saved = res.data
                    if (wasNew && saved) {
                        this.formData = { ...this.formData, ...saved }
                        this.loadCampaignCriteria()
                        this.pillarTabIndex = 1
                    }
                    this.successFeedback(this.getI18n(i18nKey, 'TITLES.pilar'), this.getI18n(i18nKey, 'MESSAGES.pilar_salvo'))
                    this.$emit('refresh')
                    if (!wasNew) {
                        this.handleClose()
                    } else {
                        this.isEdit = false
                        this.loadCampaignCriteria()
                    }
                })
                .catch((err) => {
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        err?.response?.data?.message || err?.message || this.getI18n('RESPONSES', 'operation_not_possible')
                    )
                })
                .finally(() => { this.submitLoading = false })
        },
        ruleTypeLabel(ruleType) {
            return ruleType ? this.getI18n(i18nKey, `RULE_TYPES.${ruleType}`) || ruleType : '—'
        },
        scopeLabelForTask(task) {
            if (!task) return '—'
            if (task.product_id) return this.getI18n(i18nKey, 'SCOPE.product') || 'Product'
            if (task.product_category_id) return this.getI18n(i18nKey, 'SCOPE.product_category') || 'Category'
            if (task.brand_id) return this.getI18n(i18nKey, 'SCOPE.brand') || 'Brand'
            return this.getI18n(i18nKey, 'SCOPE.generalized') || 'Generalized'
        },
        conditionOperatorLabel(op) {
            return op ? this.getI18n(i18nKey, `CONDITION_OPERATORS.${op}`) || op : '—'
        },
        openCriterionModal(criterion) {
            const payload = criterion
                ? { ...criterion }
                : {
                    id: 0,
                    pillar_id: this.formData.id,
                    name: '',
                    rule_type: 'binary',
                    weight: 0,
                    depends_on_criterion_id: null,
                    when_dependency_missing: 'exclude',
                }
            this.$refs.criterionModal.replaceModalData(payload)
            this.$nextTick(() => this.$refs.criterionModal.showModal())
        },
        onCriterionRefresh() {
            this.loadCampaignCriteria()
            this.$emit('refresh')
        },
        openQuestionsModal(criterion) {
            this.selectedCriterionForQuestions = criterion
            this.criterionQuestions = []
            this.criterionQuestionsLoading = true
            this.questionsModalVisible = true
            this.$nextTick(() => this.loadCriterionQuestions())
        },
        loadCriterionQuestions() {
            if (!this.selectedCriterionForQuestions) {
                this.criterionQuestionsLoading = false
                return
            }
            this.criterionQuestionsLoading = true
            const criterionId = this.selectedCriterionForQuestions.id
            const translate = (suffix) => this.getI18n(i18nKey, suffix)

            const buildRows = (list, rosterList) => {
                const metaByItemId = new Map()
                ;(rosterList || []).forEach((row) => {
                    metaByItemId.set(Number(row.id), {
                        question_type: row.question_type,
                        question_subtype: row.question_subtype,
                        question_is_multiple: row.question_is_multiple,
                        choice_options: row.choice_options || [],
                    })
                })
                const byId = new Map(list.map((t) => [Number(t.id), t]))
                return list.map((t) => {
                    const pid = t.depends_on_criterion_task_id != null ? Number(t.depends_on_criterion_task_id) : null
                    const parent = pid ? byId.get(pid) : null
                    const meta = metaByItemId.get(Number(t.questionnaire_item_id)) || {
                        question_type: null,
                        question_subtype: null,
                        question_is_multiple: false,
                        choice_options: [],
                    }
                    const kind = getQuestionKind(
                        meta.question_type,
                        meta.question_is_multiple,
                        meta.question_subtype
                    )
                    const conditionValueDisplay = formatConditionValueForDisplay(
                        kind,
                        t.condition_operator,
                        t.condition_value,
                        meta.choice_options,
                        translate
                    )
                    return {
                        ...t,
                        depends_on_label: parent
                            ? (parent.label || `#${parent.id}`)
                            : (pid ? `#${pid}` : '—'),
                        conditionValueDisplay,
                    }
                })
            }

            Promise.all([
                LojaPerfeitaService.getCriterionQuestions(criterionId).catch(() => ({ data: [] })),
                LojaPerfeitaService.getById(this.campaignId).catch(() => ({ data: null })),
            ])
                .then(([tasksRes, campRes]) => {
                    const list = tasksRes.data || []
                    const qid = campRes && campRes.data && campRes.data.questionnaire_id
                    if (!qid) {
                        return buildRows(list, [])
                    }
                    return LojaPerfeitaService.getQuestionnaireTasks(qid)
                        .then((rosterRes) => buildRows(list, rosterRes.data || []))
                        .catch(() => buildRows(list, []))
                })
                .then((rows) => {
                    this.criterionQuestions = Array.isArray(rows) ? rows : []
                })
                .catch(() => {
                    this.criterionQuestions = []
                })
                .finally(() => {
                    this.criterionQuestionsLoading = false
                })
        },
        openQuestionModal(question = null) {
            const siblingSum = this.criterionQuestions
                .filter((q) => !question || Number(q.id) !== Number(question.id))
                .reduce((sum, q) => sum + effectiveCriterionTaskWeight(q), 0)
            this.questionSiblingWeightSum = siblingSum
            this.$refs.questionModal.replaceModalData(
                question
                    ? { ...question }
                    : {
                        id: 0,
                        label: '',
                        weight: 0,
                        condition_operator: 'equals',
                        condition_value: '',
                        questionnaire_item_id: null,
                        product_id: null,
                        product_category_id: null,
                        brand_id: null,
                        depends_on_criterion_task_id: null,
                        depends_on_condition_operator: 'equals',
                        depends_on_condition_value: '1',
                    }
            )
            this.$nextTick(() => this.$refs.questionModal.showModal())
        },
        openDeleteConfirm(title, body, callback) {
            this.deleteConfirmTitle = title
            this.deleteConfirmBody = body
            this.deleteConfirmCallback = callback
            this.deleteConfirmBusy = false
            this.$refs.deleteConfirmModal.show()
        },
        closeDeleteConfirm() {
            this.$refs.deleteConfirmModal.hide()
            this.deleteConfirmCallback = null
        },
        executeDeleteConfirm() {
            if (!this.deleteConfirmCallback) return
            this.deleteConfirmBusy = true
            Promise.resolve(this.deleteConfirmCallback())
                .then(() => {
                    this.closeDeleteConfirm()
                })
                .catch((err) => {
                    this.negativeFeedback(this.getI18n('RESPONSES', 'something_wrong'), err?.message)
                })
                .finally(() => {
                    this.deleteConfirmBusy = false
                })
        },
    },
}
</script>

<style scoped>
.loja-perfeita-modal-toolbar {
    gap: 0.5rem;
    overflow-x: auto;
}

.loja-perfeita-rec-line-delete {
    min-width: 2rem;
    line-height: 1;
    text-decoration: none !important;
    box-shadow: none !important;
}

.loja-perfeita-rec-line-delete:focus {
    box-shadow: none !important;
}

.loja-perfeita-rec-line-delete .fe-trash-2 {
    font-size: 1.125rem;
    vertical-align: middle;
}
</style>
